/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {reInitializeState} from '@ion/applications/src/helpers';
import {Operator} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageState} from '@/types/pages';
import {AdminAction, AsyncState} from '@/types/redux';
import {
  ADD_ADMIN_DRIVER,
  ADD_ADMIN_DRIVER_ERROR,
  ADD_ADMIN_DRIVER_REQUEST,
  ADD_ADMIN_DRIVER_RESPONSE,
  ADMIN_DRIVERS_GRID_REFRESHED,
  AUDIT_ADMIN_DRIVER,
  AUDIT_ADMIN_DRIVER_ERROR,
  AUDIT_ADMIN_DRIVER_REQUEST,
  AUDIT_ADMIN_DRIVER_RESPONSE,
  DELETE_ADMIN_DRIVER,
  DELETE_ADMIN_DRIVER_ERROR,
  DELETE_ADMIN_DRIVER_REQUEST,
  DELETE_ADMIN_DRIVER_RESPONSE,
  DELETE_ADMIN_DRIVERS,
  DELETE_ADMIN_DRIVERS_ERROR,
  DELETE_ADMIN_DRIVERS_REQUEST,
  DELETE_ADMIN_DRIVERS_RESPONSE,
  EDIT_ADMIN_DRIVER,
  EDIT_ADMIN_DRIVER_ERROR,
  EDIT_ADMIN_DRIVER_REQUEST,
  EDIT_ADMIN_DRIVER_RESPONSE,
  EDIT_ADMIN_DRIVERS,
  EDIT_ADMIN_DRIVERS_ERROR,
  EDIT_ADMIN_DRIVERS_REQUEST,
  EDIT_ADMIN_DRIVERS_RESPONSE,
  GET_ADMIN_DRIVERS_ERROR,
  GET_ADMIN_DRIVERS_REQUEST,
  GET_ADMIN_DRIVERS_RESPONSE,
  IMPORT_ADMIN_DRIVERS,
  INITIALIZE_ADMIN_DRIVERS,
  MANAGE_ADMIN_DRIVER,
  MANAGE_ADMIN_DRIVER_ERROR,
  MANAGE_ADMIN_DRIVER_REQUEST,
  MANAGE_ADMIN_DRIVER_RESPONSE,
  RE_INITIALIZE_ADMIN_DRIVERS,
  SET_ADMIN_DRIVERS_GRID_FILTER_MODEL,
  SET_GRID_COLUMNS,
  UPDATE_GRID_COLUMNS
} from '../actions/admin-drivers';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../helpers';

export interface AdminDriversActionState extends AsyncState {
  selected: Operator | Operator[] | null;
  result: Operator | Operator[] | null;
  isEditing: boolean;
  record?: Operator | Operator[] | null;
};

export interface AdminDriversImportState {
  isImporting: boolean;
  result: Operator | Operator[] | null;
};

export interface AdminDriversState extends PageState {
  drivers: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {},
  },
  add: AdminDriversActionState,
  edit: AdminDriversActionState,
  manage: AdminDriversActionState,
  audit: AdminDriversActionState,
  destroy: AdminDriversActionState,
  bulkEdit: AdminDriversActionState,
  bulkDelete: AdminDriversActionState,
  bulkImport: AdminDriversImportState
};

export interface AdminDriversAction extends AdminAction {
  driver?: Operator;
  drivers?: Operator[];
};

// defines the user preference key for the admin drivers page ...
const PREFERENCE = 'admin-drivers';

// defines the initial state for the reducer ...
export const initialState: AdminDriversState = {
  isInitialized : false,
  drivers       : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {}
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  manage : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  audit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkEdit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkDelete : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    record    : null
  },
  bulkImport : {
    isImporting : false,
    result      : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_DRIVERS] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [RE_INITIALIZE_ADMIN_DRIVERS] : draft => {
    reInitializeState(draft, {initialState});
  },
  [SET_GRID_COLUMNS] : ({grid, drivers}: AdminDriversState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : drivers.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminDriversState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [ADMIN_DRIVERS_GRID_REFRESHED] : (
    {add, edit, manage, audit, destroy, bulkEdit, bulkDelete, bulkImport}: AdminDriversState
  ) => {
    add.result = null;
    edit.result = null;
    manage.result = null;
    audit.result = null;
    destroy.result = null;
    bulkEdit.result = null;
    bulkDelete.result = null;
    bulkImport.result = null;
  },
  [GET_ADMIN_DRIVERS_REQUEST] : ({drivers}: AdminDriversState) => {
    drivers.loading = true;
  },
  [GET_ADMIN_DRIVERS_RESPONSE] : ({drivers}: AdminDriversState) => {
    drivers.loading = false;
  },
  [GET_ADMIN_DRIVERS_ERROR] : (
    {drivers}: AdminDriversState, {error}: AdminDriversAction
  ) => {
    drivers.loading = false;
    drivers.error   = error;
  },
  [IMPORT_ADMIN_DRIVERS] : ({bulkImport}: AdminDriversState, {show, result}: AdminDriversAction) => {
    bulkImport.isImporting = show;
    bulkImport.result = result;
  },
  [ADD_ADMIN_DRIVER] : ({add}: AdminDriversState, {show}: AdminDriversAction) => {
    add.isEditing = show;
    add.result    = null;
  },
  [ADD_ADMIN_DRIVER_REQUEST] : ({add}: AdminDriversState) => {
    add.loading = true;
    add.error   = add.result = null;
  },
  [ADD_ADMIN_DRIVER_RESPONSE] : ({add}: AdminDriversState, {driver}: AdminDriversAction) => {
    add.loading   = false;
    add.isEditing = false;
    add.result    = driver;
  },
  [ADD_ADMIN_DRIVER_ERROR] : ({add}: AdminDriversState, {error}: AdminDriversAction) => {
    add.loading   = false;
    add.error     = error;
  },
  [EDIT_ADMIN_DRIVER] : ({edit}: AdminDriversState, {driver, show}: AdminDriversAction) => {
    edit.selected  = show ? driver : null;
    edit.isEditing = show;
    edit.result    = null;
  },
  [EDIT_ADMIN_DRIVER_REQUEST] : ({edit}: AdminDriversState) => {
    edit.loading = true;
    edit.error   = edit.result = null;
  },
  [EDIT_ADMIN_DRIVER_RESPONSE] : ({edit}: AdminDriversState, {driver}: AdminDriversAction) => {
    edit.loading   = false;
    edit.isEditing = false;
    edit.result    = driver;
  },
  [EDIT_ADMIN_DRIVER_ERROR] : ({edit}: AdminDriversState, {error}: AdminDriversAction) => {
    edit.loading   = false;
    edit.error     = error;
  },
  [MANAGE_ADMIN_DRIVER] : ({manage}: AdminDriversState, {driver, show}: AdminDriversAction) => {
    manage.selected  = show ? driver : null;
    manage.isEditing = show;
    manage.result    = null;
  },
  [MANAGE_ADMIN_DRIVER_REQUEST] : ({manage}: AdminDriversState) => {
    manage.loading = true;
    manage.error   = manage.result = null;
  },
  [MANAGE_ADMIN_DRIVER_RESPONSE] : ({manage}: AdminDriversState, {driver}: AdminDriversAction) => {
    manage.loading   = false;
    manage.isEditing = false;
    manage.result    = driver;
  },
  [MANAGE_ADMIN_DRIVER_ERROR] : ({manage}: AdminDriversState, {error}: AdminDriversAction) => {
    manage.loading   = false;
    manage.error     = error;
  },
  [AUDIT_ADMIN_DRIVER] : ({audit}: AdminDriversState, {driver, show}: AdminDriversAction) => {
    audit.selected  = show ? driver : null;
    audit.isEditing = show;
    audit.result    = null;
  },
  [AUDIT_ADMIN_DRIVER_REQUEST] : ({audit}: AdminDriversState) => {
    audit.loading = true;
    audit.error   = audit.result = null;
  },
  [AUDIT_ADMIN_DRIVER_RESPONSE] : ({audit}: AdminDriversState, {result}: AdminDriversAction) => {
    audit.loading   = false;
    audit.isEditing = false;
    audit.result    = result;
  },
  [AUDIT_ADMIN_DRIVER_ERROR] : ({audit}: AdminDriversState, {error}: AdminDriversAction) => {
    audit.loading   = false;
    audit.error     = error;
  },
  [DELETE_ADMIN_DRIVER] : ({destroy}: AdminDriversState, {driver, show}: AdminDriversAction) => {
    destroy.selected  = show ? driver : null;
    destroy.isEditing = show;
    destroy.result    = null;
  },
  [DELETE_ADMIN_DRIVER_REQUEST] : ({destroy}: AdminDriversState) => {
    destroy.loading = true;
    destroy.error   = destroy.result = null;
  },
  [DELETE_ADMIN_DRIVER_RESPONSE] : ({destroy}: AdminDriversState, {driver}: AdminDriversAction) => {
    destroy.loading    = false;
    destroy.isEditing  = false;
    destroy.result     = driver;
  },
  [DELETE_ADMIN_DRIVER_ERROR] : ({destroy}: AdminDriversState, {error}: AdminDriversAction) => {
    destroy.loading   = false;
    destroy.error     = error;
  },
  [EDIT_ADMIN_DRIVERS] : ({bulkEdit}: AdminDriversState, {drivers, show}: AdminDriversAction) => {
    bulkEdit.selected  = show ? drivers : null;
    bulkEdit.isEditing = show;
    bulkEdit.result    = null;
  },
  [EDIT_ADMIN_DRIVERS_REQUEST] : ({bulkEdit}: AdminDriversState) => {
    bulkEdit.loading = true;
    bulkEdit.error   = bulkEdit.result = null;
  },
  [EDIT_ADMIN_DRIVERS_RESPONSE] : ({bulkEdit}: AdminDriversState, {drivers}: AdminDriversAction) => {
    bulkEdit.loading   = false;
    bulkEdit.isEditing = false;
    bulkEdit.result    = drivers;
  },
  [EDIT_ADMIN_DRIVERS_ERROR] : ({bulkEdit}: AdminDriversState, {error}: AdminDriversAction) => {
    bulkEdit.loading   = false;
    bulkEdit.error     = error;
  },
  [DELETE_ADMIN_DRIVERS] : ({bulkDelete}: AdminDriversState, {drivers, show}: AdminDriversAction) => {
    bulkDelete.selected  = show ? drivers : null;
    bulkDelete.isEditing = show;
    bulkDelete.result    = null;
    bulkDelete.record    = null;
  },
  [DELETE_ADMIN_DRIVERS_REQUEST] : ({bulkDelete}: AdminDriversState) => {
    bulkDelete.loading = true;
    bulkDelete.error   = bulkDelete.result = bulkDelete.record = null;
  },
  [DELETE_ADMIN_DRIVERS_RESPONSE] : (
    {bulkDelete}: AdminDriversState, {drivers}: AdminDriversAction
  ) => {
    bulkDelete.loading   = false;
    bulkDelete.isEditing = false;
    bulkDelete.result    = drivers;
    bulkDelete.record    = drivers;
  },
  [DELETE_ADMIN_DRIVERS_ERROR] : ({bulkDelete}: AdminDriversState, {error}: AdminDriversAction) => {
    bulkDelete.loading   = false;
    bulkDelete.error     = error;
  },
  [SET_ADMIN_DRIVERS_GRID_FILTER_MODEL] : ({grid}: AdminDriversState, {model}) => {
    grid.filterModel = model;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminDriversState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
